import { useState, useEffect } from "react";
import { Upload, CheckCircle, AlertCircle, Loader2, X, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.docx', '.doc', '.csv', '.zip'];
const ACCEPT_STRING = ".pdf,.xlsx,.xls,.docx,.doc,.csv,.zip,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/csv,application/zip";
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const contractorTypes = [
  "Civil contractor",
  "Drainage / utilities contractor",
  "General contractor",
  "Specialist subcontractor",
  "Other"
];

const tenderStatuses = [
  "Substantially complete - ready for review",
  "Still being priced - early review",
  "Early stage - scope check only"
];

const regions = [
  { value: "nz", label: "New Zealand", currency: "NZ$595 excl. GST" },
  { value: "au", label: "Australia", currency: "A$545 excl. GST" }
];

export default function TenderForm() {
  const [formData, setFormData] = useState({
    contact_name: "",
    company: "",
    email: "",
    phone: "",
    region: "nz",
    tender_name: "",
    contractor_type: "",
    approximate_value: "",
    closing_date: "",
    closing_time: "",
    timezone: "",
    requested_completion: "",
    tender_status: "",
    primary_concern: "",
    nda_required: false,
    acknowledge_limitation: false
  });

  const [files, setFiles] = useState({
    tender_documents: [],
    estimate: [],
    quotations: [],
    programme: [],
    qualifications: []
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState(null);

  // Detect timezone
  useEffect(() => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setFormData(prev => ({ ...prev, timezone: detectedTimezone }));
    
    // Auto-detect region
    if (detectedTimezone.includes("Australia")) {
      setFormData(prev => ({ ...prev, region: "au" }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type not allowed: ${file.name}. Allowed: PDF, XLSX, DOCX, CSV, ZIP`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large: ${file.name}. Maximum size is 50MB`;
    }
    return null;
  };

  const handleFileChange = (category, e) => {
    const newFiles = Array.from(e.target.files);
    const errors = [];
    const validFiles = [];

    newFiles.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setFiles(prev => ({
      ...prev,
      [category]: [...prev[category], ...validFiles]
    }));
    setError(null);
  };

  const removeFile = (category, index) => {
    setFiles(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.acknowledge_limitation) {
      setError("Please acknowledge the independent review limitation to proceed.");
      return;
    }

    setSubmitting(true);

    try {
      // Create form data for multipart upload
      const submitData = new FormData();
      
      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // Add files from all categories
      const allFiles = [
        ...files.tender_documents,
        ...files.estimate,
        ...files.quotations,
        ...files.programme,
        ...files.qualifications
      ];

      allFiles.forEach(file => {
        submitData.append('files', file);
      });

      // Create file categories metadata
      const fileCategories = {
        tender_documents: files.tender_documents.map(f => f.name),
        estimate: files.estimate.map(f => f.name),
        quotations: files.quotations.map(f => f.name),
        programme: files.programme.map(f => f.name),
        qualifications: files.qualifications.map(f => f.name)
      };
      submitData.append('file_categories', JSON.stringify(fileCategories));

      const response = await axios.post(`${API}/tender-review-request`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setReferenceNumber(response.data.reference_number || response.data.id);
      setSubmitted(true);
    } catch (err) {
      console.error("Submission error:", err);
      setError(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="tender-form"
        className="py-16 md:py-24 border-t-4 border-black bg-white"
        data-testid="tender-form-success"
      >
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <CheckCircle size={64} className="mx-auto mb-6 text-green-600" />
          <h2 className="font-heading font-bold text-3xl uppercase mb-4">
            Tender Submitted
          </h2>
          <p className="font-body text-zinc-600 mb-6">
            Thank you for your submission. We've received your tender review request.
          </p>
          {referenceNumber && (
            <div className="bg-zinc-100 border-2 border-black p-6 mb-8">
              <p className="font-mono text-sm text-zinc-500 uppercase mb-2">Reference Number</p>
              <p className="font-mono text-2xl font-bold">{referenceNumber}</p>
            </div>
          )}
          <div className="bg-zinc-50 p-6 border border-zinc-200 text-left">
            <h3 className="font-heading font-bold text-lg uppercase mb-3">What happens next?</h3>
            <ul className="space-y-2 font-body text-zinc-700">
              <li>• We'll review your submission for completeness</li>
              <li>• Scope, fee (excl. GST) and turnaround will be confirmed before commencement</li>
              <li>• Payment is due after acceptance and before work begins</li>
              <li>• You'll receive a confirmation email shortly</li>
              <li>• If we need additional information, we'll be in touch</li>
            </ul>
          </div>
        </div>
      </section>
    );
  }

  const FileUploadSection = ({ category, label, description, inputId }) => (
    <div className="space-y-3">
      <label 
        htmlFor={inputId}
        className="font-heading font-bold text-sm uppercase tracking-wider block"
      >
        {label}
      </label>
      <p className="font-body text-sm text-zinc-500">{description}</p>
      
      {/* File list */}
      {files[category].length > 0 && (
        <div className="space-y-2 mb-3">
          {files[category].map((file, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-zinc-100 border border-zinc-200">
              <FileText size={18} className="text-zinc-500" />
              <span className="font-mono text-sm flex-grow truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(category, index)}
                className="text-zinc-400 hover:text-red-600 transition-colors"
                aria-label={`Remove ${file.name}`}
              >
                <X size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      <label 
        htmlFor={inputId}
        className="flex items-center gap-3 p-4 border-2 border-dashed border-zinc-300 cursor-pointer hover:border-black transition-colors"
      >
        <Upload size={20} className="text-zinc-400" />
        <span className="font-mono text-sm text-zinc-600">Click to upload files</span>
        <input
          type="file"
          id={inputId}
          name={category}
          multiple
          accept={ACCEPT_STRING}
          onChange={(e) => handleFileChange(category, e)}
          className="hidden"
          aria-describedby={`${inputId}-desc`}
        />
      </label>
      <p id={`${inputId}-desc`} className="sr-only">
        Upload {label}. Supported formats: PDF, XLSX, DOCX, CSV, ZIP. Maximum 50MB per file.
      </p>
    </div>
  );

  return (
    <section
      id="tender-form"
      className="py-16 md:py-24 border-t-4 border-black bg-white"
      data-testid="tender-form-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
            Submit
          </span>
          <h2
            className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mt-4"
            data-testid="tender-form-title"
          >
            Submit a Tender for Review
          </h2>
          <p className="font-body text-zinc-600 mt-4 max-w-2xl mx-auto">
            Complete the form below to submit your tender for independent review. Scope, fee and turnaround will be confirmed before commencement.
          </p>
        </div>

        {/* Form with explicit method and action */}
        <form 
          method="POST"
          action={`${API}/tender-review-request`}
          onSubmit={handleSubmit} 
          className="space-y-8"
          encType="multipart/form-data"
        >
          {/* Error display */}
          {error && (
            <div className="p-4 bg-red-50 border-2 border-red-200 flex items-start gap-3" role="alert">
              <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
              <p className="font-body text-red-700 text-sm whitespace-pre-wrap">{error}</p>
            </div>
          )}

          {/* Contact Details */}
          <fieldset className="border-2 border-black p-6 md:p-8">
            <legend className="font-heading font-bold text-xl uppercase px-2">Contact Details</legend>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <label 
                  htmlFor="tender-contact-name"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Contact Name *
                </label>
                <input
                  type="text"
                  id="tender-contact-name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleInputChange}
                  required
                  autoComplete="name"
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-contact-name"
                />
              </div>
              <div>
                <label 
                  htmlFor="tender-company"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Company *
                </label>
                <input
                  type="text"
                  id="tender-company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  autoComplete="organization"
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-company"
                />
              </div>
              <div>
                <label 
                  htmlFor="tender-email"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="tender-email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  autoComplete="email"
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-email"
                />
              </div>
              <div>
                <label 
                  htmlFor="tender-phone"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Phone *
                </label>
                <input
                  type="tel"
                  id="tender-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  autoComplete="tel"
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-phone"
                />
              </div>
              <div className="md:col-span-2">
                <fieldset>
                  <legend className="font-heading font-bold text-sm uppercase tracking-wider mb-2">
                    Country/Region *
                  </legend>
                  <div className="flex gap-4">
                    {regions.map(region => (
                      <label key={region.value} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          id={`tender-region-${region.value}`}
                          name="region"
                          value={region.value}
                          checked={formData.region === region.value}
                          onChange={handleInputChange}
                          className="w-5 h-5 border-2 border-black"
                        />
                        <span className="font-body">
                          {region.label} <span className="text-zinc-500">({region.currency})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            </div>
          </fieldset>

          {/* Tender Details */}
          <fieldset className="border-2 border-black p-6 md:p-8">
            <legend className="font-heading font-bold text-xl uppercase px-2">Tender Details</legend>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div className="md:col-span-2">
                <label 
                  htmlFor="tender-name"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Tender / Project Name *
                </label>
                <input
                  type="text"
                  id="tender-name"
                  name="tender_name"
                  value={formData.tender_name}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-name"
                />
              </div>
              <div>
                <label 
                  htmlFor="tender-contractor-type"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Contractor Type *
                </label>
                <select
                  id="tender-contractor-type"
                  name="contractor_type"
                  value={formData.contractor_type}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none bg-white"
                  data-testid="tender-contractor-type"
                >
                  <option value="">Select...</option>
                  <option value="Civil contractor">Civil contractor</option>
                  <option value="Drainage / utilities contractor">Drainage / utilities contractor</option>
                  <option value="General contractor">General contractor</option>
                  <option value="Specialist subcontractor">Specialist subcontractor</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label 
                  htmlFor="tender-value"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Approximate Tender Value
                </label>
                <input
                  type="text"
                  id="tender-value"
                  name="approximate_value"
                  value={formData.approximate_value}
                  onChange={handleInputChange}
                  placeholder="e.g., $500,000 - $1M"
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-value"
                />
              </div>
              <div>
                <label 
                  htmlFor="tender-closing-date"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Tender Closing Date *
                </label>
                <input
                  type="date"
                  id="tender-closing-date"
                  name="closing_date"
                  value={formData.closing_date}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-closing-date"
                />
              </div>
              <div>
                <label 
                  htmlFor="tender-closing-time"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Closing Time & Timezone *
                </label>
                <div className="flex gap-2">
                  <input
                    type="time"
                    id="tender-closing-time"
                    name="closing_time"
                    value={formData.closing_time}
                    onChange={handleInputChange}
                    required
                    className="flex-1 h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                    data-testid="tender-closing-time"
                  />
                  <input
                    type="text"
                    id="tender-timezone"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    placeholder="Timezone"
                    aria-label="Timezone"
                    className="w-32 h-12 px-3 border-2 border-black focus:shadow-brutal transition-shadow outline-none text-sm"
                  />
                </div>
              </div>
              <div>
                <label 
                  htmlFor="tender-completion"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Requested Review Completion
                </label>
                <input
                  type="text"
                  id="tender-completion"
                  name="requested_completion"
                  value={formData.requested_completion}
                  onChange={handleInputChange}
                  placeholder="e.g., 24 hours before close"
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-completion"
                />
              </div>
              <div>
                <label 
                  htmlFor="tender-status"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Current Tender Status *
                </label>
                <select
                  id="tender-status"
                  name="tender_status"
                  value={formData.tender_status}
                  onChange={handleInputChange}
                  required
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none bg-white"
                  data-testid="tender-status"
                >
                  <option value="">Select...</option>
                  <option value="Substantially complete - ready for review">Substantially complete - ready for review</option>
                  <option value="Still being priced - early review">Still being priced - early review</option>
                  <option value="Early stage - scope check only">Early stage - scope check only</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label 
                  htmlFor="tender-concern"
                  className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
                >
                  Primary Concern (one sentence)
                </label>
                <input
                  type="text"
                  id="tender-concern"
                  name="primary_concern"
                  value={formData.primary_concern}
                  onChange={handleInputChange}
                  placeholder="What's the main thing you want us to check?"
                  className="w-full h-12 px-4 border-2 border-black focus:shadow-brutal transition-shadow outline-none"
                  data-testid="tender-concern"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="tender-nda"
                    name="nda_required"
                    checked={formData.nda_required}
                    onChange={handleInputChange}
                    className="w-5 h-5 border-2 border-black"
                  />
                  <span className="font-body">
                    NDA or specific confidentiality requirements apply
                  </span>
                </label>
              </div>
            </div>
          </fieldset>

          {/* File Uploads */}
          <fieldset className="border-2 border-black p-6 md:p-8">
            <legend className="font-heading font-bold text-xl uppercase px-2">Review Pack Documents</legend>
            <p className="font-body text-zinc-600 text-sm mb-6 mt-2">
              Supported formats: PDF, XLSX, DOCX, CSV, ZIP. Maximum 50MB per file.
            </p>
            
            <div className="bg-zinc-50 border border-zinc-200 p-4 mb-6">
              <p className="font-body text-sm text-zinc-700">
                <strong>Confidentiality:</strong> All uploaded documents are treated as strictly confidential. Files are stored securely and are not publicly accessible.
              </p>
            </div>

            <div className="space-y-8">
              <FileUploadSection
                category="tender_documents"
                inputId="upload-tender-documents"
                label="Tender / Scope Documents"
                description="Specifications, drawings list, scope of works, conditions"
              />
              <FileUploadSection
                category="estimate"
                inputId="upload-estimate"
                label="Current Estimate"
                description="Your pricing spreadsheet, estimate workbook or estimate PDF"
              />
              <FileUploadSection
                category="quotations"
                inputId="upload-quotations"
                label="Key Supplier & Subcontract Quotations"
                description="Quotes you're relying on for the tender"
              />
              <FileUploadSection
                category="programme"
                inputId="upload-programme"
                label="Programme or Methodology"
                description="If available - construction sequence, gantt chart, methodology"
              />
              <FileUploadSection
                category="qualifications"
                inputId="upload-qualifications"
                label="Qualifications, Assumptions or Exclusions"
                description="Any documented qualifications or assumptions"
              />
            </div>
          </fieldset>

          {/* Acknowledgement */}
          <div className="border-2 border-black p-6 md:p-8 bg-zinc-50">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="tender-acknowledge"
                name="acknowledge_limitation"
                checked={formData.acknowledge_limitation}
                onChange={handleInputChange}
                required
                className="w-5 h-5 border-2 border-black mt-0.5 flex-shrink-0"
                data-testid="tender-acknowledge"
              />
              <span className="font-body text-sm">
                I acknowledge that this is an independent commercial review only. It is not legal advice, engineering advice, a quantity surveyor's report or professional certification, a complete independent remeasurement, or a guarantee of tender success or project profitability. The final tender and submission decision remain with me/my company. *
              </span>
            </label>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-mono text-sm text-zinc-500">
              By submitting, you agree to our{" "}
              <Link to="/privacy" className="underline hover:text-black">Privacy Policy</Link>{" "}
              and{" "}
              <Link to="/terms" className="underline hover:text-black">Terms</Link>.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="bg-black text-white h-14 px-8 uppercase font-bold tracking-widest border-2 border-black btn-brutal flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="tender-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit for Review"
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
